import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, MessageSquare, User, Download, Copy, Check } from 'lucide-react';

const TRANSLATIONS = {
  "hu-HU": {
    "appTitle": "QR Kód Generátor",
    "appDescription": "Generálj QR kódokat URL-ekhez, szöveghez és kapcsolattartási információkhoz",
    "urlTab": "URL",
    "textTab": "Szöveg", 
    "contactTab": "Kapcsolat",
    "enterUrl": "URL megadása",
    "enterText": "Szöveg megadása",
    "contactInformation": "Kapcsolattartási információk",
    "websiteUrl": "Weboldal URL",
    "urlPlaceholder": "pelda.hu vagy https://pelda.hu",
    "urlHelp": "Add meg a weboldal URL-jét. Ha nem tartalmazza a http://-t, automatikusan hozzáadjuk a https://-t.",
    "textContent": "Szöveg tartalom",
    "textPlaceholder": "Írj be bármilyen szöveget a QR kód generálásához...",
    "firstName": "Keresztnév",
    "firstNamePlaceholder": "János",
    "lastName": "Vezetéknév", 
    "lastNamePlaceholder": "Kovács",
    "phoneNumber": "Telefonszám",
    "phonePlaceholder": "+36 30 123 4567",
    "emailAddress": "E-mail cím",
    "emailPlaceholder": "kovacs.janos@pelda.hu",
    "organization": "Szervezet",
    "organizationPlaceholder": "Cég neve",
    "website": "Weboldal",
    "websitePlaceholder": "https://pelda.hu",
    "clearAllFields": "Minden mező törlése",
    "generatedQrCode": "Generált QR kód",
    "scanQrCode": "Olvasd be ezt a QR kódot a készülékeddel",
    "fillFormPrompt": "Töltsd ki az űrlapot a QR kód generálásához",
    "download": "Letöltés",
    "copyData": "Adat másolása",
    "copied": "Másolva!",
    "qrCodeData": "QR kód adatok:",
    "footerText": "Generálj QR kódokat azonnal • Nincs adattárolás • Ingyenes",
    "qrCodeAlt": "Generált QR kód"
  },
  "en-US": {
    "appTitle": "QR Code Generator",
    "appDescription": "Generate QR codes for URLs, text, and contact information",
    "urlTab": "URL",
    "textTab": "Text",
    "contactTab": "Contact",
    "enterUrl": "Enter URL",
    "enterText": "Enter Text", 
    "contactInformation": "Contact Information",
    "websiteUrl": "Website URL",
    "urlPlaceholder": "example.com or https://example.com",
    "urlHelp": "Enter a website URL. If you don't include http://, we'll add https:// automatically.",
    "textContent": "Text Content",
    "textPlaceholder": "Enter any text to generate QR code...",
    "firstName": "First Name",
    "firstNamePlaceholder": "John",
    "lastName": "Last Name",
    "lastNamePlaceholder": "Doe", 
    "phoneNumber": "Phone Number",
    "phonePlaceholder": "+1 ^(555^) 123-4567",
    "emailAddress": "Email Address",
    "emailPlaceholder": "john.doe@example.com",
    "organization": "Organization",
    "organizationPlaceholder": "Company Name",
    "website": "Website",
    "websitePlaceholder": "https://example.com",
    "clearAllFields": "Clear All Fields",
    "generatedQrCode": "Generated QR Code",
    "scanQrCode": "Scan this QR code with your device",
    "fillFormPrompt": "Fill in the form to generate your QR code",
    "download": "Download",
    "copyData": "Copy Data",
    "copied": "Copied!",
    "qrCodeData": "QR Code Data:",
    "footerText": "Generate QR codes instantly • No data stored • Free to use",
    "qrCodeAlt": "Generated QR Code"
  }
};

const browserLocale = navigator.languages?.[0] || navigator.language || 'hu-HU';
const findMatchingLocale = (locale) => {
  if (TRANSLATIONS[locale]) return locale;
  const lang = locale.split('-')[0];
  const match = Object.keys(TRANSLATIONS).find(key => key.startsWith(lang + '-'));
  return match || 'hu-HU';
};
const locale = findMatchingLocale(browserLocale);
const t = (key) => TRANSLATIONS[locale]?.[key] || TRANSLATIONS['hu-HU'][key] || key;

const QRCodeGenerator = () => {
  const [activeTab, setActiveTab] = useState('url');
  const [qrData, setQrData] = useState('');
  const [copied, setCopied] = useState(false);
  const qrContainerRef = useRef(null);

  const [urlInput, setUrlInput] = useState('');
  const [textInput, setTextInput] = useState('');
  const [contactInfo, setContactInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    organization: '',
    url: ''
  });

  const generateFallbackQR = useCallback((text) => {
    if (!qrContainerRef.current) return;
    qrContainerRef.current.innerHTML = '';
    const img = document.createElement('img');
    const encodedData = encodeURIComponent(text);
    img.src = 'https://chart.googleapis.com/chart?chs=300x300&cht=qr&chl=' + encodedData + '&choe=UTF-8';
    img.alt = t('qrCodeAlt');
    img.className = 'w-full h-auto rounded-xl shadow-lg bg-white p-4';
    img.style.maxWidth = '300px';
    img.style.height = 'auto';
    img.onerror = () => {
      img.src = 'https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=' + encodedData + '&format=png&margin=10';
    };
    qrContainerRef.current.appendChild(img);
  }, []);

  const createQR = useCallback((text) => {
    if (!qrContainerRef.current) return;
    try {
      qrContainerRef.current.innerHTML = '';
      const canvas = document.createElement('canvas');
      qrContainerRef.current.appendChild(canvas);
      new window.QRious({
        element: canvas,
        value: text,
        size: 300,
        background: 'white',
        foreground: 'black',
        level: 'M'
      });
      canvas.className = 'w-full h-auto rounded-xl shadow-lg bg-white';
      canvas.style.maxWidth = '300px';
      canvas.style.height = 'auto';
    } catch (error) {
      console.error('Error creating QR code:', error);
      generateFallbackQR(text);
    }
  }, [generateFallbackQR]);

  const generateQRCode = useCallback(async (text) => {
    if (!text.trim()) {
      if (qrContainerRef.current) {
        qrContainerRef.current.innerHTML = '';
      }
      return;
    }
    try {
      if (!window.QRious) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrious/4.0.2/qrious.min.js';
        script.onload = () => { createQR(text); };
        document.head.appendChild(script);
      } else {
        createQR(text);
      }
    } catch (error) {
      console.error('Error loading QR library:', error);
      generateFallbackQR(text);
    }
  }, [createQR, generateFallbackQR]);

  const formatUrl = (url) => {
    if (!url.trim()) return '';
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return 'https://' + url;
    }
    return url;
  };

  const generateVCard = (contact) => {
    // Android kompatibilis VCard UTF-8 enkódolással
    const encodeVCardText = (text) => {
      if (!text) return '';
      // UTF-8 karakterek átalakítása Android kompatibilis formátumba
      return text
        .replace(/á/g, 'a').replace(/Á/g, 'A')
        .replace(/é/g, 'e').replace(/É/g, 'E')
        .replace(/í/g, 'i').replace(/Í/g, 'I')
        .replace(/ó/g, 'o').replace(/Ó/g, 'O')
        .replace(/ö/g, 'o').replace(/Ö/g, 'O')
        .replace(/ő/g, 'o').replace(/Ő/g, 'O')
        .replace(/ú/g, 'u').replace(/Ú/g, 'U')
        .replace(/ü/g, 'u').replace(/Ü/g, 'U')
        .replace(/ű/g, 'u').replace(/Ű/g, 'U');
    };
    const firstName = encodeVCardText(contact.firstName);
    const lastName = encodeVCardText(contact.lastName);
    const organization = encodeVCardText(contact.organization);
    const vcard = 'BEGIN:VCARD\nVERSION:3.0\nFN:' + firstName + ' ' + lastName + '\nN:' + lastName + ';' + firstName + ';;;\nORG:' + organization + '\nTEL:' + contact.phone + '\nEMAIL:' + contact.email + '\nURL:' + contact.url + '\nEND:VCARD';
    return vcard;
  };

  useEffect(() => {
    let data = '';
    switch (activeTab) {
      case 'url':
        data = formatUrl(urlInput);
        break;
      case 'text':
        data = textInput;
        break;
      case 'contact':
        if (contactInfo.firstName || contactInfo.lastName || contactInfo.phone || contactInfo.email) {
          data = generateVCard(contactInfo);
        }
        break;
      default:
        data = '';
    }
    setQrData(data);
    generateQRCode(data);
  }, [activeTab, urlInput, textInput, contactInfo, generateQRCode]);

  useEffect(() => {
    const sendHeight = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ height: height }, '*');
    };
    sendHeight();
    const observer = new MutationObserver(sendHeight);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
    window.addEventListener('resize', sendHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, []);

  const downloadQRCode = () => {
    if (!qrData) return;
    const canvas = qrContainerRef.current?.querySelector('canvas');
    const img = qrContainerRef.current?.querySelector('img');
    if (canvas) {
      const link = document.createElement('a');
      link.download = 'qr-kod-' + activeTab + '.png';
      link.href = canvas.toDataURL();
      link.click();
    } else if (img) {
      const link = document.createElement('a');
      link.download = 'qr-kod-' + activeTab + '.png';
      link.href = img.src;
      link.click();
    }
  };

  const copyToClipboard = async () => {
    if (qrData) {
      try {
        await navigator.clipboard.writeText(qrData);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  };

  const resetForm = () => {
    setUrlInput('');
    setTextInput('');
    setContactInfo({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      organization: '',
      url: ''
    });
    setQrData('');
    if (qrContainerRef.current) {
      qrContainerRef.current.innerHTML = '';
    }
  };

  const tabs = [
    { id: 'url', label: t('urlTab'), icon: Link },
    { id: 'text', label: t('textTab'), icon: MessageSquare },
    { id: 'contact', label: t('contactTab'), icon: User }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-emerald-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-white shadow-md">
            <img src="/vista-verde-192.png?v=2" alt="Vista Verde" className="w-10 h-10" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">
            {t('appTitle')}
          </h1>
          <p className="text-gray-600 text-lg">{t('appDescription')}</p>
        </div>
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={'flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-all duration-200 ' + (activeTab === tab.id ? 'text-green-600 border-b-2 border-green-600 bg-green-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50')}>
                    <IconComponent className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                  {activeTab === 'url' && t('enterUrl')}
                  {activeTab === 'text' && t('enterText')}
                  {activeTab === 'contact' && t('contactInformation')}
                </h2>
                {activeTab === 'url' && <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('websiteUrl')}</label><input type="url" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} placeholder={t('urlPlaceholder')} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" /><p className="text-xs text-gray-500 mt-1">{t('urlHelp')}</p></div>}
                {activeTab === 'text' && <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('textContent')}</label><textarea value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder={t('textPlaceholder')} rows={4} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 resize-none" /></div>}
                {activeTab === 'contact' && <div className="space-y-4"><div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium text-gray-700 mb-2">{t('firstName')}</label><input type="text" value={contactInfo.firstName} onChange={(e) => setContactInfo({...contactInfo, firstName: e.target.value})} placeholder={t('firstNamePlaceholder')} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" /></div><div><label className="block text-sm font-medium text-gray-700 mb-2">{t('lastName')}</label><input type="text" value={contactInfo.lastName} onChange={(e) => setContactInfo({...contactInfo, lastName: e.target.value})} placeholder={t('lastNamePlaceholder')} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" /></div></div><div><label className="block text-sm font-medium text-gray-700 mb-2">{t('phoneNumber')}</label><input type="tel" value={contactInfo.phone} onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})} placeholder={t('phonePlaceholder')} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" /></div><div><label className="block text-sm font-medium text-gray-700 mb-2">{t('emailAddress')}</label><input type="email" value={contactInfo.email} onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})} placeholder={t('emailPlaceholder')} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" /></div><div><label className="block text-sm font-medium text-gray-700 mb-2">{t('organization')}</label><input type="text" value={contactInfo.organization} onChange={(e) => setContactInfo({...contactInfo, organization: e.target.value})} placeholder={t('organizationPlaceholder')} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" /></div><div><label className="block text-sm font-medium text-gray-700 mb-2">{t('website')}</label><input type="url" value={contactInfo.url} onChange={(e) => setContactInfo({...contactInfo, url: e.target.value})} placeholder={t('websitePlaceholder')} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200" /></div></div>}
                <button onClick={resetForm} className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium">{t('clearAllFields')}</button>
              </div>
              <div className="flex flex-col items-center space-y-6">
                <h2 className="text-2xl font-semibold text-gray-800">{t('generatedQrCode')}</h2>
                <div className="bg-gray-50 rounded-2xl p-8 w-full max-w-sm">
                  {qrData ? <div className="text-center"><div ref={qrContainerRef} className="flex justify-center"></div><p className="text-sm text-gray-600 mt-4">{t('scanQrCode')}</p></div> : <div className="text-center py-16"><img src="/vista-verde-192.png?v=2" alt="Vista Verde" className="w-16 h-16 opacity-40 mx-auto mb-4" /><p className="text-gray-500">{t('fillFormPrompt')}</p></div>}
                </div>
                {qrData && <div className="flex gap-4 w-full max-w-sm"><button onClick={downloadQRCode} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-medium shadow-lg"><Download className="w-4 h-4" />{t('download')}</button><button onClick={copyToClipboard} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium">{copied ? <><Check className="w-4 h-4 text-green-600" />{t('copied')}</> : <><Copy className="w-4 h-4" />{t('copyData')}</>}</button></div>}
                {qrData && <div className="w-full max-w-sm"><h3 className="text-sm font-medium text-gray-700 mb-2">{t('qrCodeData')}</h3><div className="bg-gray-100 rounded-lg p-3 text-xs text-gray-600 max-h-32 overflow-y-auto"><pre className="whitespace-pre-wrap break-words">{qrData}</pre></div></div>}
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mt-8 text-gray-500 text-sm"><p>{t('footerText')}</p></div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
